import { KIT, siteOrigin } from "@/lib/developer-markdown";
import { DOOR_LABEL, type Door } from "./card";

/** The packages' version in the kit (public/developers/kit), as sdks/ says it (kit.test.ts holds them together). */
export const KIT_VERSION = "0.1.8";

/** Each installing door's file in the kit, named as npm pack and pip wheel name it. */
export const KIT_FILES = {
  ts: `vestra-sdk-${KIT_VERSION}.tgz`,
  py: `vestra-${KIT_VERSION}-py3-none-any.whl`,
  cli: `vestra-cli-${KIT_VERSION}.tgz`,
} as const;

/** How each door installs until it is published (ruling K13): its file, straight from the kit of the site at origin. */
export function installsAt(
  origin: string
): ReadonlyArray<{ door: Door; line: string }> {
  const kit = (door: keyof typeof KIT_FILES) =>
    `${origin}${KIT}/${KIT_FILES[door]}`;
  return [
    { door: "ts", line: `npm install ${kit("ts")} && npm pkg set type=module` },
    { door: "py", line: `pip install ${kit("py")}` },
    { door: "cli", line: `npm install -g ${kit("cli")}` },
  ];
}

/** This build's install lines, at its own origin: a staging build installs from staging.vestra.ai, production from vestra.ai. */
export const INSTALLS = installsAt(siteOrigin());

/** Python's wheel goes in a virtual environment: many Pythons refuse a system-wide pip install (PEP 668). */
export const VENV = "python3 -m venv .venv && source .venv/bin/activate";

/** The install lines as one shell block, each saying its door: Python's after its virtual environment. */
const INSTALL_LINES = INSTALLS.flatMap((i) => [
  ...(i.door === "py"
    ? [{ line: VENV, says: `${DOOR_LABEL.py}: a virtual environment first` }]
    : []),
  { line: i.line, says: DOOR_LABEL[i.door] },
]);
const INSTALL_WIDTH = Math.max(...INSTALL_LINES.map(({ line }) => line.length));
export const INSTALL = INSTALL_LINES.map(
  ({ line, says }) => `${line.padEnd(INSTALL_WIDTH)} # ${says}`
).join("\n");

/** What the site says about installing, the docs line owed for ESM only among it. */
export const INSTALL_NOTES: readonly string[] = [
  "TypeScript: not on npm yet, so install it straight from this site. It needs Node 20 or later and brings one dependency, `yaml`. Set `VESTRA_API_KEY` to a test key, and `VESTRA_BASE_URL` to `https://api-staging.vestra.ai` while `/v1` is in preview.",
  'It is ESM only: the install line sets `"type": "module"` in your `package.json`, so a `.ts` file runs with `npx tsx file.ts`, top-level `await` and all. A CommonJS project can `require()` it on Node 20.19 or 22.12 and later; on an older Node, use `await import()`.',
  "Python: not on PyPI yet, so install the wheel straight from this site, in a virtual environment (`python3 -m venv .venv`, then `source .venv/bin/activate`): many Pythons refuse a system-wide `pip install`. It needs Python 3.11 or later (check `python3 --version`), and brings `httpx`, `attrs` and `pyyaml`. `Vestra` blocks on every call; `AsyncVestra` has the same names, each awaited.",
  "The CLI: not on npm yet, so install it straight from this site, globally. It needs Node 20 or later. `vestra login` keeps your key on this computer, and `VESTRA_API_KEY` and `VESTRA_BASE_URL` win over it. Every line takes `--user`, and `vestra <line> --help` says the rest.",
];
